'use client';

import { useEffect, useRef, TextareaHTMLAttributes, useImperativeHandle, MouseEvent, type Ref } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { Field, FieldError } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from '@/components/ui/input-group';
import { useCanAutoFocus } from '@/hooks/useCanAutoFocus';

type TextAreaInputProps = {
  ref?: Ref<HTMLTextAreaElement>;
  withAutoScroll?: boolean;
  isLoading?: boolean;
  loadingPlaceholder?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  triggerValue?: string;
  defaultValue?: string;
  showCross?: boolean;
  onRemoveValue?: () => void;
  showCounter?: boolean;
  maxCounterLength?: number;
  minCounterLength?: number;
  counterClassName?: string;
  renderMin?: (val: string) => string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextAreaInput({
  ref,
  withAutoScroll = false,
  isLoading = false,
  loadingPlaceholder: _loadingPlaceholder = '',
  label = '',
  error = '',
  showCross = false,
  onRemoveValue,
  showCounter = false,
  maxCounterLength = 2000,
  minCounterLength,
  counterClassName,
  renderMin,
  ...props
}: TextAreaInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canAutoFocus = useCanAutoFocus();

  useImperativeHandle(ref, () => textareaRef.current!);

  const resolvedValue = String(props.value ?? props.defaultValue ?? '');
  const valueLength = resolvedValue.length;
  const hasValue = valueLength > 0;
  const hasLabel = label.length > 0;
  const hasError = error.length > 0;
  const showClear = showCross && hasValue;

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onRemoveValue?.();
    textareaRef.current?.focus();
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const updateHeight = () => {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, 80)}px`;
    };

    updateHeight();
    textarea.addEventListener('input', updateHeight);

    return () => {
      textarea.removeEventListener('input', updateHeight);
    };
  }, [resolvedValue]);

  return (
    <Field data-invalid={hasError || undefined} className='gap-[4px] data-[invalid=true]:text-current'>
      {isLoading ? (
        <div
          className='relative box-border min-h-[80px] w-full overflow-hidden rounded-[8px] border border-brand-input bg-transparent px-[16px] pt-[28px] pb-[9px] lg:min-h-[140px]'
          aria-live='polite'
          aria-busy='true'
        >
          {hasLabel && (
            <p className='absolute start-[17px] top-[8px] text-[12px] leading-[14.4px] text-text-secondary'>{label}</p>
          )}
          <div className='mt-[6px] flex flex-col gap-[10px]'>
            <div className='h-[13px] w-[88%] animate-pulse rounded-full bg-[#E8E8E8]' />
            <div className='h-[13px] w-[72%] animate-pulse rounded-full bg-[#E8E8E8] [animation-delay:200ms]' />
            <div className='h-[13px] w-[80%] animate-pulse rounded-full bg-[#E8E8E8] [animation-delay:400ms]' />
          </div>
          <div className='pointer-events-none absolute inset-0 -translate-x-full animate-shimmer bg-linear-to-r from-transparent via-white/60 to-transparent rtl:translate-x-full' />
        </div>
      ) : (
        <InputGroup className='min-h-[80px] items-start rounded-[8px] has-[[data-slot=input-group-control]:focus-visible]:ring-[3px]'>
          <InputGroupTextarea
            onFocus={(e) => {
              props.onFocus?.(e);
              if (withAutoScroll && textareaRef.current) {
                const { top } = textareaRef.current.getBoundingClientRect();
                const scrollElem = document.querySelector('#scroll-target');
                scrollElem?.scrollTo({
                  top: scrollElem.scrollTop + (top - 16),
                  behavior: 'smooth',
                });
              }
            }}
            placeholder={label}
            aria-invalid={hasError}
            className={cn(
              'peer min-h-[80px] overflow-hidden px-[16px] pb-[9px] text-[16px] leading-[20px] md:text-[16px] lg:min-h-[140px]',
              hasLabel ? 'pt-[28px] placeholder:text-transparent' : 'pt-[9px]',
              showClear && 'pe-[8px]',
            )}
            rows={1}
            ref={textareaRef}
            {...props}
            autoFocus={props.autoFocus && canAutoFocus}
          />
          {hasLabel && (
            <span
              className={cn(
                'pointer-events-none absolute start-[17px] text-text-secondary transition-[top,font-size,line-height] duration-100 ease-linear',
                'top-[8px] text-[12px] leading-[14.4px]',
                'peer-placeholder-shown:top-[18.5px] peer-placeholder-shown:text-[16px] peer-placeholder-shown:leading-[20px]',
                'peer-focus:top-[8px] peer-focus:text-[12px] peer-focus:leading-[14.4px]',
              )}
            >
              {label}
            </span>
          )}
          {showClear && (
            <InputGroupAddon align='inline-end' className='pe-[10px] pt-[10px] pb-0'>
              <InputGroupButton size='icon-sm' className='hover:bg-transparent active:scale-100' onClick={handleClear}>
                <Image src='/assets/icons/close-grey.svg' height={24} width={24} alt='' />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
      )}

      {(hasError || showCounter) && (
        <div className='mt-[12px] flex h-[20px] items-center gap-[22px] md:mt-0'>
          {hasError && <FieldError className='error-form text-[14px] leading-[20px] text-red-day'>{error}</FieldError>}

          {showCounter && (!!minCounterLength || !!maxCounterLength) && (
            <div
              className={cn('text-[14px] leading-[20px]', counterClassName, {
                'text-red-day': valueLength > maxCounterLength,
              })}
            >
              {renderMin ? (
                renderMin(`${valueLength}/${maxCounterLength}`)
              ) : (
                <>
                  {' '}
                  {valueLength} / {minCounterLength ?? maxCounterLength}{' '}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Field>
  );
}

TextAreaInput.displayName = 'TextAreaInput';
